import { useEffect, useRef, useState } from 'react'

import {
  dropTabSelection,
  readTabSelection,
  useWorkspaceTabsStore,
  writeTabSelection,
} from '#/shared/lib/workspace-tabs'

export interface UseTableRowSelectionResult {
  selectedIds: string[]
  selectedCount: number
  resetSelection: () => void
  onSelectionChange: (ids: string[]) => void
}

/**
 * Row selection for the list pages. Pass a `scopeKey` naming the result set
 * (list identity, status tab, page, date range); every change is snapshotted
 * per *workspace tab* under that key.
 *
 * Two kinds of change get opposite treatment:
 * - A workspace-tab switch restores the target tab's snapshot — the selection
 *   survives the unmount/param swap the switch causes.
 * - An in-tab view change (status tab, page, date range) is a hard reset: the
 *   snapshot is dropped, so flipping back does NOT bring the selection back.
 *
 * Feed `selectedIds` into the DataTable's controlled `selectedIds` prop.
 */
export function useTableRowSelection(
  scopeKey: string,
): UseTableRowSelectionResult {
  const activeTabId = useWorkspaceTabsStore((state) => state.activeTabId)
  const [selectedIds, setSelectedIds] = useState<string[]>(() =>
    readTabSelection(activeTabId, scopeKey),
  )

  const lastTab = useRef(activeTabId)
  const lastScope = useRef(scopeKey)
  // A workspace-tab switch reaches this hook in two steps — the tab id flips
  // first, the URL params land afterwards — so the scope change that follows a
  // switch is part of the switch, not a user view change. This flag spans that
  // window; it's armed only when the switch's own render couldn't restore yet.
  const settling = useRef(false)

  useEffect(() => {
    const tabChanged = lastTab.current !== activeTabId
    const scopeChanged = lastScope.current !== scopeKey
    if (!tabChanged && !scopeChanged) return
    lastTab.current = activeTabId
    lastScope.current = scopeKey

    const restored = readTabSelection(activeTabId, scopeKey)
    if (tabChanged) {
      settling.current = restored.length === 0
    } else if (settling.current) {
      settling.current = false
    } else {
      // In-tab view change: reset for good, don't just hide the snapshot.
      dropTabSelection(activeTabId)
      setSelectedIds([])
      return
    }
    setSelectedIds(restored)
  }, [activeTabId, scopeKey])

  const select = (ids: string[]) => {
    setSelectedIds(ids)
    writeTabSelection(activeTabId, scopeKey, ids)
  }

  return {
    selectedIds,
    selectedCount: selectedIds.length,
    resetSelection: () => select([]),
    onSelectionChange: select,
  }
}
